import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const ModeContext = createContext(null);

export function useMode() {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}

// Pages associated with each mode
const CSPS_PAGES = ['/securite', '/csps'];
const OPC_PAGES = ['/dashboard', '/chantiers', '/reserves', '/planning', '/rapports', '/documents-opc'];

export function ModeProvider({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const profileType = user?.profile_type || 'opc_csps';
  const [mode, setMode] = useState('opc');
  const [isManualMode, setIsManualMode] = useState(false);

  const hasOPC = profileType === 'opc' || profileType === 'opc_csps';
  const hasCSPS = profileType === 'csps' || profileType === 'opc_csps';
  const canSwitch = profileType === 'opc_csps';

  // Auto-detect mode based on current page (unless manually set)
  useEffect(() => {
    if (!isManualMode) {
      const path = location.pathname;
      if (CSPS_PAGES.some(p => path.startsWith(p))) {
        if (hasCSPS) setMode('csps');
      } else if (OPC_PAGES.some(p => path.startsWith(p))) {
        if (hasOPC) setMode('opc');
      }
    }
  }, [location.pathname, isManualMode, hasOPC, hasCSPS]);

  // Ensure mode is valid for profile on user change
  useEffect(() => {
    if (profileType === 'opc' && mode === 'csps') setMode('opc');
    if (profileType === 'csps' && mode === 'opc') setMode('csps');
  }, [profileType, mode]);

  const toggleMode = () => {
    if (!canSwitch) return;
    setIsManualMode(true);
    setMode(prev => prev === 'opc' ? 'csps' : 'opc');
  };

  const setModeManually = (newMode) => {
    if (newMode === 'csps' && !hasCSPS) return;
    if (newMode === 'opc' && !hasOPC) return;
    setIsManualMode(true);
    setMode(newMode);
  };

  const resetToAutoMode = () => {
    setIsManualMode(false);
  };

  const value = {
    mode,
    isOPC: mode === 'opc',
    isCSPS: mode === 'csps',
    hasOPC,
    hasCSPS,
    canSwitch,
    profileType,
    toggleMode,
    setMode: setModeManually,
    resetToAutoMode,
    isManualMode
  };

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  );
}
